/**
 * BuildYourOwnView.js — "Build your own" (custom report Type 3).
 * ---------------------------------------------------------------------------
 * Renders a fixed A4 page from an ordered array of user-composed blocks. Each
 * block is a self-fetching mini-component (bar chart, map, audience breakdown,
 * MP bar, note, or demographics) bound to the report's area + scope.
 *
 * Two modes:
 *   - read-only (preview / export): clean A4, blocks laid out in a flex-wrap
 *     grid (half-width blocks pair into a row, full-width span).
 *   - editable (the live builder canvas): each block is draggable for reorder,
 *     shows remove + width-toggle controls, and accepts drops from the sidebar
 *     palette (insert-before a block, or append via the trailing drop tile).
 *
 * A block spec: { id, type, groupId?, chart?, text?, width: "full"|"half" }
 *   type: "bar" | "map" | "audiences" | "mp" | "note" | "demographics"
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AspectRatioRoundedIcon from "@mui/icons-material/AspectRatioRounded";
import {
  loadGroupData, BarCompareConstNat, WardGroupStaticMapWithBase,
  ConstituencyGroupStaticMap, MPCard, loadFullMpData, DemographicBlock,
} from "./utils/metricsClient";
import { getPolConfig } from "./utils/polAggregators";
import { computeTopAudiences } from "./utils/computeTopAudiences";

const INK = "#0f172a";
const INK2 = "#64748b";
const INK3 = "#94a3b8";
const BORDER = "#e2e8f0";
const ACCENT = "#0f9fab";

// The canvas is a fine 12-column × MAX_ROWS grid. Rows are FLEXIBLE (1fr) so
// they always divide the page height exactly — a block dragged to the last row
// reaches the page bottom with no leftover gap or overflow. Width presets:
// ¼=3, ½=6, ¾=9, full=12 columns; height is a free row span (1..MAX_ROWS).
export const GRID_COLS = 12;
const ROW_GAP = 6;
export const MAX_ROWS = 24;   // the page is divided into this many equal rows
export const WIDTH_PRESETS = [
  { span: 3, label: "¼" }, { span: 6, label: "½" }, { span: 9, label: "¾" }, { span: 12, label: "Full" },
];

// Default height (in rows) for a freshly-added block, by type (~pixel targets).
export const DEFAULT_ROWSPAN = { map: 8, audiences: 7, demographics: 7, mp: 4, bar: 5, note: 2 };
export const defaultRowSpan = (type) => DEFAULT_ROWSPAN[type] || 4;

export const BUILD_BLOCK_TYPES = [
  { type: "bar",          label: "Bar chart",          needsQuestion: true  },
  { type: "map",          label: "Map",                needsQuestion: true  },
  { type: "audiences",    label: "Audience breakdown", needsQuestion: true  },
  { type: "mp",           label: "MP bar",             needsQuestion: false },
  { type: "note",         label: "Note",               needsQuestion: false },
  { type: "demographics", label: "Demographics",       needsQuestion: false },
];

/* ── Self-fetching block: question as a const-vs-national bar chart ───────── */
function BarBlock({ groupId, areaCode, national, constName }) {
  const [data, setData] = useState([]);
  useEffect(() => {
    let live = true;
    loadGroupData({ groupId, areaCode: national ? "GB" : areaCode })
      .then((d) => { if (live) setData(national ? (d || []).map((x) => ({ ...x, constituency: x.national ?? x.constituency, national: null })) : (d || [])); })
      .catch(() => { if (live) setData([]); });
    return () => { live = false; };
  }, [groupId, areaCode, national]);
  return <BarCompareConstNat data={data} constName={national ? "Great Britain" : (constName || "")} hideNational={national} />;
}

/* ── Self-fetching block: question as a map ───────────────────────────────── */
function MapBlock({ groupId, areaCode, national }) {
  if (national) return <ConstituencyGroupStaticMap groupId={groupId} />;
  return <WardGroupStaticMapWithBase groupId={groupId} areaCode={areaCode} />;
}

/* ── Self-fetching block: compact top-audiences for a question ────────────── */
function AudienceBlock({ groupId, areaCode, national }) {
  const group = useMemo(() => { try { return getPolConfig().groupsById?.[groupId] || null; } catch { return null; } }, [groupId]);
  const [rows, setRows]   = useState([]);
  const [aud, setAud]     = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    loadGroupData({ groupId, areaCode: national ? "GB" : areaCode })
      .then((d) => {
        if (!live) return;
        const mapped = national ? (d || []).map((x) => ({ ...x, constituency: x.national ?? x.constituency })) : (d || []);
        // Top 3 responses by value (skip don't-know / none-of-these noise).
        const top = mapped
          .filter((r) => Number.isFinite(Number(r.constituency)) && !/dontknow|dont_know|none/i.test(String(r.key)))
          .sort((a, b) => Number(b.constituency) - Number(a.constituency))
          .slice(0, 3);
        setRows(top);
      })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [groupId, areaCode, national]);

  useEffect(() => {
    if (!group || !rows.length) return;
    let live = true;
    const controller = new AbortController();
    computeTopAudiences({
      areaCode, national,
      groupKeys: group.keys || [],
      options: rows.map((r) => ({ key: r.key, sumKeys: [r.key] })),
      theme: group.theme || (String(group.keys?.[0] || "").startsWith("ind:") ? "ind" : "pol"),
      schema: group.schema || "tracking",
      normaliseOverOptions: !!group.normalise_over_options,
      waves: group.waves || null,
      signal: controller.signal,
    }).then((res) => { if (live) { setAud(res || {}); setLoading(false); } })
      .catch(() => { if (live) setLoading(false); });
    return () => { live = false; controller.abort(); };
  }, [group, rows, areaCode, national]);

  if (!rows.length) return <BlockLoading label="audiences" />;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {rows.map((r) => {
        const combined = aud[r.key]?.combined;
        return (
          <Box key={r.key} sx={{ borderLeft: `3px solid ${r.color || ACCENT}`, pl: "8px", py: "2px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: INK }}>{group?.labels?.[r.key] || r.label || r.key}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: r.color || ACCENT }}>{Number(r.constituency).toFixed(0)}%</Typography>
            </Box>
            <Typography sx={{ fontSize: 9.5, color: INK2 }}>
              {loading ? "Computing top audience…" : combined
                ? <>Strongest: <b>{combined.label}</b> · {combined.score.toFixed(0)}%</>
                : "No breakdown available"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/* ── Self-fetching block: MP bar (from the political report) ──────────────── */
function MpBlock({ constName }) {
  const [mp, setMp] = useState(null);
  useEffect(() => {
    let live = true;
    if (constName) loadFullMpData(constName).then((d) => { if (live) setMp(d); }).catch(() => {});
    return () => { live = false; };
  }, [constName]);
  return <MPCard mpData={mp} />;
}

function BlockLoading({ label }) {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 60 }}>
      <Typography sx={{ fontSize: 10, color: INK3 }}>Loading {label}…</Typography>
    </Box>
  );
}

/* ── One block's inner content (no chrome) ────────────────────────────────── */
function BlockBody({ block, ctx }) {
  const { areaCode, national, constName, extras } = ctx;
  switch (block.type) {
    case "bar":       return <BarBlock groupId={block.groupId} areaCode={areaCode} national={national} constName={constName} />;
    case "map":       return <MapBlock groupId={block.groupId} areaCode={areaCode} national={national} />;
    case "audiences": return <AudienceBlock groupId={block.groupId} areaCode={areaCode} national={national} />;
    case "mp":        return national ? <Typography sx={{ fontSize: 10, color: INK3 }}>MP bar is constituency-only.</Typography> : <MpBlock constName={constName} />;
    case "demographics": return <DemographicBlock constCode={national ? undefined : areaCode} constName={national ? "Great Britain" : constName} national={national} />;
    case "note":
      return (
        <Typography sx={{ fontSize: 12.5, color: INK, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {block.text || "Double-click in the sidebar to add note text."}
        </Typography>
      );
    default: return null;
  }
}

const BLOCK_TITLE = (block, group) => {
  if (block.type === "note") return null;
  if (block.type === "mp") return "Member of Parliament";
  if (block.type === "demographics") return "Demographic profile";
  const q = group?.label || group?.fulltext || block.groupId;
  const suffix = block.type === "map" ? " — map" : block.type === "audiences" ? " — top audiences" : "";
  return `${q}${suffix}`;
};

/* ── The A4 page ──────────────────────────────────────────────────────────── */
export default function BuildYourOwnView({
  blocks = [],
  title = "Custom report",
  constName,
  areaCode,
  national = false,
  height = "1123px",
  extras = {},
  edit = null,   // when set: { onUpdateGeom, onCanvasDrop, onRemove, onCycleSpan }
}) {
  const groupsById = useMemo(() => { try { return getPolConfig().groupsById || {}; } catch { return {}; } }, []);
  const ctx = { areaCode, national, constName, extras };
  const editable = !!edit;
  const gridRef = useRef(null);
  const [resizingId, setResizingId] = useState(null);

  // Geometry helpers. Each block carries an explicit grid position (col,row) and
  // size (span cols, rowSpan rows). Legacy blocks without a position default to 1,1.
  const spanOf = (b) => b.span || (b.width === "half" ? 6 : GRID_COLS);
  const rowsOf = (b) => b.rowSpan || DEFAULT_ROWSPAN[b.type] || 1;
  const colOf  = (b) => b.col || 1;
  const rowOf  = (b) => b.row || 1;
  const clamp  = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const gridMetrics = () => {
    const rect = gridRef.current ? gridRef.current.getBoundingClientRect() : { width: 700, height: 900, left: 0, top: 0 };
    // Track/pitch = (total + one gap) / count, so a drag of one pitch = one cell.
    return { rect, colW: (rect.width + ROW_GAP) / GRID_COLS, rowH: (rect.height + ROW_GAP) / MAX_ROWS };
  };

  // Sidebar-palette drop → place a block at the grid cell under the pointer.
  const canvasDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { rect, colW, rowH } = gridMetrics();
    const col = clamp(Math.floor((e.clientX - rect.left) / colW) + 1, 1, GRID_COLS);
    const row = clamp(Math.floor((e.clientY - rect.top) / rowH) + 1, 1, MAX_ROWS);
    edit.onCanvasDrop(col, row);
  };

  // Move (edges = null) drags the whole block; resize (edges = {left,right,top,
  // bottom}) moves those sides while the opposite side stays pinned.
  const startGeom = (e, block, edges) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const { colW, rowH } = gridMetrics();
    const startX = e.clientX, startY = e.clientY;
    const c0 = colOf(block), r0 = rowOf(block), cs0 = spanOf(block), rs0 = rowsOf(block);
    const rightEdge = c0 + cs0 - 1;   // pinned when dragging the left edge
    const bottomEdge = r0 + rs0 - 1;  // pinned when dragging the top edge
    const moving = !edges;
    setResizingId(block.id);
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const apply = (ev) => {
      const dCol = Math.round((ev.clientX - startX) / colW);
      const dRow = Math.round((ev.clientY - startY) / rowH);
      let col = c0, row = r0, span = cs0, rowSpan = rs0;
      if (moving) {
        col = clamp(c0 + dCol, 1, GRID_COLS - cs0 + 1);
        row = clamp(r0 + dRow, 1, MAX_ROWS - rs0 + 1);
      } else {
        if (edges.right)  span    = clamp(cs0 + dCol, 1, GRID_COLS - c0 + 1);
        if (edges.bottom) rowSpan = clamp(rs0 + dRow, 1, MAX_ROWS - r0 + 1);
        if (edges.left)   { col = clamp(c0 + dCol, 1, rightEdge);  span    = rightEdge - col + 1; }
        if (edges.top)    { row = clamp(r0 + dRow, 1, bottomEdge); rowSpan = bottomEdge - row + 1; }
      }
      edit.onUpdateGeom(block.id, { col, row, span, rowSpan });
    };
    const up = () => {
      setResizingId(null);
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener("mousemove", apply);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", apply);
    window.addEventListener("mouseup", up);
  };

  // Edge + corner handles. Top-right corner is omitted so it can't fight the
  // block's control buttons; the four edges + bottom corners cover resizing.
  const HANDLES = [
    { key: "n",  edges: { top: true },                 pos: { top: 0, left: 24, right: 24, height: 10, cursor: "row-resize" }, bar: { width: 30, height: 4 } },
    { key: "s",  edges: { bottom: true },              pos: { bottom: 0, left: 24, right: 24, height: 10, cursor: "row-resize" }, bar: { width: 30, height: 4 } },
    { key: "w",  edges: { left: true },                pos: { left: 0, top: 30, bottom: 18, width: 10, cursor: "col-resize" }, bar: { width: 4, height: 30 } },
    { key: "e",  edges: { right: true },               pos: { right: 0, top: 30, bottom: 18, width: 10, cursor: "col-resize" }, bar: { width: 4, height: 30 } },
    { key: "sw", edges: { bottom: true, left: true },  pos: { bottom: 0, left: 0, width: 16, height: 16, cursor: "nesw-resize" }, bar: { width: 9, height: 9 } },
    { key: "se", edges: { bottom: true, right: true }, pos: { bottom: 0, right: 0, width: 16, height: 16, cursor: "nwse-resize" }, bar: { width: 9, height: 9 } },
  ];

  const renderBlock = (block) => {
    const group = block.groupId ? groupsById[block.groupId] : null;
    const heading = BLOCK_TITLE(block, group);
    const span = spanOf(block), rowSpan = rowsOf(block);
    const active = resizingId === block.id;
    return (
      <Box
        key={block.id}
        onMouseDown={editable ? (e) => startGeom(e, block, null) : undefined}
        onDragOver={editable ? (e) => e.preventDefault() : undefined}
        onDrop={editable ? canvasDrop : undefined}
        sx={{
          position: "relative",
          gridColumn: `${colOf(block)} / span ${span}`,
          gridRow: `${rowOf(block)} / span ${rowSpan}`,
          minWidth: 0, minHeight: 0,
          border: `1px solid ${active ? ACCENT : BORDER}`,
          borderRadius: "6px",
          bgcolor: "#fff",
          p: "8px 10px",
          display: "flex",
          flexDirection: "column",
          zIndex: active ? 10 : 1,
          boxShadow: active ? "0 6px 20px rgba(15,23,42,0.16)" : "none",
          // Root stays overflow:visible so the edge handles aren't clipped; the
          // inner wrapper below clips the actual block content to its box.
          cursor: editable ? "move" : "default",
          "&:hover": { zIndex: editable ? 9 : 1 },
          "&:hover .block-controls": { opacity: editable ? 1 : 0 },
          "&:hover .block-resize": { opacity: editable ? 0.85 : 0 },
        }}
      >
        {heading && (
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ACCENT, mb: "5px", pr: editable ? "42px" : 0 }}>
            {heading}
          </Typography>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", borderRadius: "4px" }}>
          <BlockBody block={block} ctx={ctx} />
        </Box>

        {editable && (
          <Box className="block-controls" sx={{ position: "absolute", top: 5, right: 9, display: "flex", gap: "3px", opacity: 0, transition: "opacity 0.12s", zIndex: 12 }}>
            <ControlBtn title="Cycle width" onClick={() => edit.onCycleSpan(block.id)}><AspectRatioRoundedIcon sx={{ fontSize: 13 }} /></ControlBtn>
            <ControlBtn title="Remove" onClick={() => edit.onRemove(block.id)} danger><CloseRoundedIcon sx={{ fontSize: 13 }} /></ControlBtn>
          </Box>
        )}

        {/* Edge + corner resize handles (opposite side stays pinned) */}
        {editable && HANDLES.map((h) => (
          <Box
            key={h.key}
            className="block-resize"
            onMouseDown={(e) => startGeom(e, block, h.edges)}
            sx={{
              position: "absolute", ...h.pos, display: "flex", alignItems: "center", justifyContent: "center",
              opacity: active ? 1 : 0, transition: "opacity 0.12s", zIndex: 11,
            }}
          >
            <Box sx={{ ...h.bar, borderRadius: 2, bgcolor: ACCENT, boxShadow: "0 0 0 2px #fff" }} />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      id="report-content"
      sx={{
        backgroundColor: "white", color: "black",
        width: "794px", height, overflow: "hidden",
        mx: "auto", p: "14px 18px", border: "1px solid #ccc", boxSizing: "border-box",
        display: "flex", flexDirection: "column", fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Header */}
      <Box sx={{ pb: "6px", borderBottom: `1px solid ${BORDER}`, mb: "10px", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
          <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
            {national ? "National" : "Constituency"} · Custom report
          </Typography>
          <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.3 }}>
          {title || "Custom report"}
        </Typography>
        <Typography sx={{ fontSize: 10, color: INK2 }}>{national ? "Great Britain" : constName}</Typography>
      </Box>

      {/* Blocks — 12-col × MAX_ROWS grid; blocks are freely positioned + sized. */}
      <Box
        ref={gridRef}
        onDragOver={editable ? (e) => e.preventDefault() : undefined}
        onDrop={editable ? canvasDrop : undefined}
        sx={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`, gridTemplateRows: `repeat(${MAX_ROWS}, 1fr)`, gridAutoRows: `${100 / MAX_ROWS}%`, gap: `${ROW_GAP}px`, overflow: "hidden" }}
      >
        {blocks.length === 0 && editable && (
          <Box sx={{ gridColumn: "1 / -1", gridRow: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", border: `2px dashed ${BORDER}`, borderRadius: "8px", color: INK3, pointerEvents: "none" }}>
            <Typography sx={{ fontSize: 12 }}>Build a block in the sidebar, then drag it here.</Typography>
          </Box>
        )}
        {blocks.map(renderBlock)}
      </Box>
    </Box>
  );
}

function ControlBtn({ children, onClick, title, danger }) {
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseDown={(e) => e.stopPropagation()}
      draggable={false}
      title={title}
      sx={{
        width: 20, height: 20, borderRadius: "5px", display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: danger ? "rgba(220,38,38,0.1)" : "rgba(15,23,42,0.06)",
        color: danger ? "#b91c1c" : "#475569", cursor: "pointer",
        "&:hover": { bgcolor: danger ? "rgba(220,38,38,0.2)" : "rgba(15,23,42,0.12)" },
      }}
    >
      {children}
    </Box>
  );
}
